import type { JSX } from 'react';
import { useId } from 'react';
import { Field } from '@/app/ui/Field';
import { Button, Checkbox, controlStyles as c } from '@/app/ui/controls';
import { PALETTES, getPalette } from '@/catalogs/palettes';
import { TEMPLATES } from '@/catalogs/templates';
import { isSafeHexColor } from '@/core/layout/geometry';
import type { Project } from '@/core/project/types';
import type { EditorAction } from '../state';
import styles from './panels.module.css';

export type DesignPanelProps = {
  project: Project;
  dispatch: (action: EditorAction) => void;
};

export function DesignPanel({ project, dispatch }: DesignPanelProps): JSX.Element {
  const group = useId();
  const palette = getPalette(project.paletteId);

  return (
    <div className={styles.panel}>
      <Checkbox
        checked={project.output.includeDecoration}
        onChange={(include) => dispatch({ type: 'set-decoration', include })}
        label="Экспортировать QR с оформлением"
        hint="Выключено — сохраняется только код с защитным полем. Оформление при этом остаётся в проекте."
      />

      <Field label="Шаблон" hint="Шаблон меняет расположение и стиль, но никогда не меняет содержимое QR.">
        {() => (
          <div className={styles.grid} role="radiogroup" aria-label="Шаблон оформления">
            {TEMPLATES.map((template) => {
              const id = `${group}-t-${template.id}`;
              return (
                <span className={styles.templateCard} key={template.id}>
                  <input
                    className={styles.templateInput}
                    type="radio"
                    id={id}
                    name={`${group}-template`}
                    checked={project.template.id === template.id}
                    onChange={() => dispatch({ type: 'set-template', id: template.id })}
                  />
                  <label className={styles.templateLabel} htmlFor={id}>
                    <span className={styles.templateName}>{template.name}</span>
                    <span className={styles.templateDesc}>{template.description}</span>
                  </label>
                </span>
              );
            })}
          </div>
        )}
      </Field>

      <Field label="Палитра" hint="Название палитры написано рядом с образцами — цвет не единственный признак.">
        {() => (
          <>
            <div className={styles.paletteList} role="radiogroup" aria-label="Палитра">
              {PALETTES.map((item) => {
                const id = `${group}-p-${item.id}`;
                return (
                  <span className={styles.templateCard} key={item.id}>
                    <input
                      className={styles.templateInput}
                      type="radio"
                      id={id}
                      name={`${group}-palette`}
                      checked={project.paletteId === item.id}
                      onChange={() => dispatch({ type: 'set-palette', id: item.id })}
                    />
                    <label className={styles.templateLabel} htmlFor={id}>
                      <span className={styles.paletteRow}>
                        <span className={styles.swatches} aria-hidden="true">
                          <span
                            className={styles.swatch}
                            style={{
                              background: item.backgroundGradient
                                ? `linear-gradient(135deg, ${item.backgroundGradient[0]}, ${item.backgroundGradient[1]})`
                                : item.background,
                            }}
                          />
                          <span className={styles.swatch} style={{ background: item.qrForeground }} />
                          <span className={styles.swatch} style={{ background: item.accent }} />
                        </span>
                        <span className={styles.templateName}>{item.name}</span>
                      </span>
                    </label>
                  </span>
                );
              })}
            </div>
            <p className={styles.origin}>Происхождение выбранной палитры: {palette.origin}</p>
          </>
        )}
      </Field>

      <div className={styles.two}>
        <Field
          label="Цвет модулей"
          error={isSafeHexColor(project.qr.foreground) ? undefined : 'Ожидается цвет вида #RRGGBB'}
        >
          {({ id, describedBy }) => (
            <div className={c.colorRow}>
              <input
                id={id}
                aria-describedby={describedBy}
                className={c.colorInput}
                type="color"
                value={isSafeHexColor(project.qr.foreground) ? project.qr.foreground : '#000000'}
                onChange={(e) =>
                  dispatch({ type: 'set-qr-color', role: 'foreground', value: e.target.value })
                }
              />
              <input
                className={`${c.input} ${c.mono}`}
                type="text"
                aria-label="Цвет модулей в формате HEX"
                value={project.qr.foreground}
                onChange={(e) =>
                  dispatch({ type: 'set-qr-color', role: 'foreground', value: e.target.value })
                }
              />
            </div>
          )}
        </Field>

        <Field
          label="Фон под кодом"
          hint="По умолчанию белый — так поле и защитная зона остаются безопасными."
        >
          {({ id, describedBy }) => (
            <div className={c.colorRow}>
              <input
                id={id}
                aria-describedby={describedBy}
                className={c.colorInput}
                type="color"
                value={isSafeHexColor(project.qr.background) ? project.qr.background : '#FFFFFF'}
                onChange={(e) =>
                  dispatch({ type: 'set-qr-color', role: 'background', value: e.target.value })
                }
              />
              <Button
                variant="ghost"
                disabled={project.qr.background.toUpperCase() === '#FFFFFF'}
                onClick={() =>
                  dispatch({ type: 'set-qr-color', role: 'background', value: '#FFFFFF' })
                }
              >
                Вернуть белый
              </Button>
            </div>
          )}
        </Field>
      </div>
    </div>
  );
}
